import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { apiErrorText, readApiError, readApiErrorResponse } from "@/lib/api-error";
import { apiFetch } from "@/services/http/api-fetch";

type QuizOption = { id: string; label: string };
type QuizQuestion = { id: string; prompt: string; options: QuizOption[] };

type ActiveAttempt = {
  id: string;
  expiresAt: string;
  remainingSec: number;
  timedOut: boolean;
};

type QuizState = "not_started" | "in_progress" | "expired" | "submitted";

type QuizPayload = {
  canAttempt: boolean;
  state: QuizState;
  passScorePct: number | null;
  timeLimitSec: number | null;
  questions: QuizQuestion[];
  attempt: { scorePct: number; passed: boolean; submittedAt: string | Date | null } | null;
  activeAttempt: ActiveAttempt | null;
};

type SubmitResult = {
  scorePct: number;
  passed: boolean;
  passScorePct?: number | null;
  timedOut?: boolean;
};

const ERROR_OPTIONS = { namespace: "quiz.errors" } as const;

function formatTimer(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

type QuizPlayerProps = {
  lessonId: string;
};

/**
 * LRN-6: a timed quiz shows its rules and starts only on "Start quiz"; when time runs out the
 * answers already chosen are submitted, and with none chosen nothing is (no empty 0%).
 * LRN-7: the pass mark is shown up front, the result says passed / not passed, and a failed or
 * expired attempt explains that the course needs a pass and the instructor can reset it.
 */
export function QuizPlayer({ lessonId }: QuizPlayerProps) {
  const t = useTranslations("quiz");
  const tRoot = useTranslations();
  const [quiz, setQuiz] = useState<QuizPayload | null>(null);
  const [selections, setSelections] = useState<Record<string, string>>({});
  const [remainingSec, setRemainingSec] = useState<number | null>(null);
  /**
   * LEARN-12: when the attempt ends, on THIS device's clock — taken from the server's
   * `remainingSec` when the payload arrives, so a device clock that is minutes off (the server's
   * `expiresAt` is absolute) neither loses the answers nor shortens the exam.
   */
  const [deadlineMs, setDeadlineMs] = useState<number | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const expiryHandled = useRef(false);

  const applyPayload = useCallback((payload: QuizPayload) => {
    setQuiz(payload);
    expiryHandled.current = false;
    setRemainingSec(payload.activeAttempt ? payload.activeAttempt.remainingSec : null);
    setDeadlineMs(
      payload.state === "in_progress" && payload.activeAttempt
        ? Date.now() + payload.activeAttempt.remainingSec * 1000
        : null,
    );
  }, []);

  useEffect(() => {
    let active = true;
    // Aborts in flight when the student leaves; see ISSUES_LOG 256.
    const controller = new AbortController();
    setLoading(true);
    setLoadError(null);
    setError(null);
    setSelections({});
    setTimedOut(false);

    void (async () => {
      try {
        const res = await apiFetch(`/v1/lessons/${lessonId}/quiz`, {
          signal: controller.signal,
        });
        if (!res.ok) throw await readApiErrorResponse(res);
        const json = (await res.json()) as { data: QuizPayload };
        if (!active) return;
        applyPayload(json.data);
      } catch (err) {
        if (!active || (err as Error)?.name === "AbortError") return;
        setLoadError(apiErrorText(err, tRoot, "quiz.loadFailed", ERROR_OPTIONS));
        setQuiz(null);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
      controller.abort();
    };
  }, [applyPayload, lessonId, tRoot]);

  const markExpired = useCallback(() => {
    setQuiz((current) =>
      current ? { ...current, state: "expired", canAttempt: false, activeAttempt: null } : current,
    );
  }, []);

  const submitAnswers = useCallback(
    async (options?: { auto?: boolean }) => {
      if (!quiz || busy) return;
      const answers = quiz.questions
        .filter((question) => !options?.auto || selections[question.id])
        .map((question) => ({ questionId: question.id, optionId: selections[question.id] }));
      if (answers.length === 0) {
        markExpired();
        return;
      }

      setBusy(true);
      setError(null);
      try {
        const res = await apiFetch(`/v1/lessons/${lessonId}/quiz/submit`, {
          method: "POST",
          body: JSON.stringify({ answers }),
        });
        if (!res.ok) throw await readApiErrorResponse(res);
        const result = ((await res.json()) as { data: SubmitResult }).data;
        if (options?.auto || result.timedOut) setTimedOut(true);
        setQuiz((current) =>
          current
            ? {
                ...current,
                state: "submitted",
                canAttempt: false,
                activeAttempt: null,
                passScorePct: result.passScorePct ?? current.passScorePct,
                attempt: {
                  scorePct: result.scorePct,
                  passed: result.passed,
                  submittedAt: new Date().toISOString(),
                },
              }
            : current,
        );
      } catch (err) {
        if (readApiError(err)?.code === "ATTEMPT_EXPIRED") {
          markExpired();
        } else {
          setError(apiErrorText(err, tRoot, "quiz.submitFailed", ERROR_OPTIONS));
        }
      } finally {
        setBusy(false);
      }
    },
    [busy, lessonId, markExpired, quiz, selections, tRoot],
  );

  async function startQuiz() {
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch(`/v1/lessons/${lessonId}/quiz/start`, { method: "POST" });
      if (!res.ok) throw await readApiErrorResponse(res);
      applyPayload(((await res.json()) as { data: QuizPayload }).data);
    } catch (err) {
      setError(apiErrorText(err, tRoot, "quiz.startFailed", ERROR_OPTIONS));
    } finally {
      setBusy(false);
    }
  }

  const runningDeadline = quiz?.state === "in_progress" ? deadlineMs : null;
  useEffect(() => {
    if (runningDeadline == null) return;
    const tick = () => {
      const next = Math.max(0, Math.ceil((runningDeadline - Date.now()) / 1000));
      setRemainingSec(next);
    };
    tick();
    const intervalId = window.setInterval(tick, 1000);
    return () => window.clearInterval(intervalId);
  }, [runningDeadline]);

  useEffect(() => {
    if (quiz?.state !== "in_progress" || remainingSec == null || remainingSec > 0) return;
    if (expiryHandled.current) return;
    expiryHandled.current = true;
    void submitAnswers({ auto: true });
  }, [quiz?.state, remainingSec, submitAnswers]);

  if (loading) {
    return <p className="text-sm text-[var(--text-muted)]">{t("loading")}</p>;
  }

  if (loadError) {
    return (
      <p role="alert" className="text-sm text-[var(--danger)]" data-testid="quiz-error">
        {loadError}
      </p>
    );
  }

  if (!quiz) {
    return <p className="text-sm text-[var(--text-muted)]">{t("unavailable")}</p>;
  }

  const passMark =
    quiz.passScorePct != null ? (
      <p className="text-sm text-[var(--text-secondary)]" data-testid="quiz-pass-mark">
        {t("passMark", { pct: quiz.passScorePct })}
      </p>
    ) : null;

  const errorLine = error ? (
    <p role="alert" className="text-sm text-[var(--danger)]" data-testid="quiz-error">
      {error}
    </p>
  ) : null;

  if (quiz.state === "submitted" && quiz.attempt) {
    const { scorePct, passed } = quiz.attempt;
    return (
      <section data-testid="quiz-result" className="space-y-2">
        <h2 className="text-lg font-medium">{t("submitted")}</h2>
        {timedOut ? (
          <p className="text-sm text-[var(--accent-ink)]" data-testid="quiz-timed-out">
            {t("timedOut")}
          </p>
        ) : null}
        <p className="text-[var(--text-secondary)]" data-testid="quiz-score">
          {t("score", { score: scorePct })}
        </p>
        <p
          className={
            passed
              ? "font-medium text-[var(--success,var(--text-heading))]"
              : "font-medium text-[var(--danger)]"
          }
          data-testid="quiz-outcome"
        >
          {passed ? t("passed") : t("notPassed")}
        </p>
        {passMark}
        {!passed ? (
          <p className="text-sm text-[var(--text-secondary)]" data-testid="quiz-must-pass">
            {t("mustPassToComplete")}
          </p>
        ) : null}
      </section>
    );
  }

  if (quiz.state === "expired") {
    return (
      <section data-testid="quiz-expired" className="space-y-2" role="status">
        <h2 className="text-lg font-medium">{t("expiredTitle")}</h2>
        <p className="text-sm text-[var(--text-secondary)]">{t("expiredBody")}</p>
        {passMark}
      </section>
    );
  }

  if (quiz.state === "not_started" && quiz.timeLimitSec != null) {
    return (
      <section className="space-y-3" data-testid="quiz-intro-panel">
        <p className="text-sm text-[var(--text-secondary)]" data-testid="quiz-intro">
          {t("timeLimitIntro", { minutes: Math.max(1, Math.round(quiz.timeLimitSec / 60)) })}
        </p>
        {passMark}
        {errorLine}
        <button
          type="button"
          onClick={() => void startQuiz()}
          disabled={busy || !quiz.canAttempt}
          className="rounded bg-[var(--ink-9)] px-4 py-2 text-sm text-white disabled:opacity-50"
          data-testid="quiz-start"
        >
          {busy ? t("starting") : t("start")}
        </button>
      </section>
    );
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void submitAnswers();
      }}
      className="space-y-6"
      data-testid="quiz-player"
    >
      {passMark}
      {quiz.timeLimitSec != null && remainingSec != null ? (
        <p
          className="text-sm font-medium text-[var(--text-secondary)]"
          data-testid="quiz-timer"
          aria-live="polite"
        >
          {t("timeRemaining", { time: formatTimer(remainingSec) })}
        </p>
      ) : null}
      {quiz.questions.map((question) => (
        <fieldset key={question.id} className="space-y-2">
          <legend className="font-medium">{question.prompt}</legend>
          {question.options.map((option) => (
            <label key={option.id} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name={`question-${question.id}`}
                value={option.id}
                checked={selections[question.id] === option.id}
                onChange={() =>
                  setSelections((current) => ({ ...current, [question.id]: option.id }))
                }
                data-testid={`quiz-option-${option.id}`}
              />
              {option.label}
            </label>
          ))}
        </fieldset>
      ))}
      {errorLine}
      <button
        type="submit"
        disabled={busy}
        className="rounded bg-[var(--ink-9)] px-4 py-2 text-sm text-white disabled:opacity-50"
        data-testid="quiz-submit"
      >
        {busy ? t("submitting") : t("submit")}
      </button>
    </form>
  );
}
